import {
  Autocomplete,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Popper,
  TextField,
  Typography
} from "@mui/material";
/* eslint-disable react/no-unknown-property -- project-standard Cypress selectors */
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import axios from "axios";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { useIsMobile } from "../../hooks/useIsMobile";
import StatusBadge from "../ui/status_badge";
import { statusMetadata } from "../ui/status_metadata";
import {
  appointmentClientNumber,
  appointmentDestination,
  appointmentMatchedPeer,
  appointmentPickup,
  appointmentRecurrence,
  appointmentUnit
} from "./appointment_requests_calendar_utils";
import useAppointmentRequestActions from "./use_appointment_request_actions";

const MANAGER_OR_ADMIN_ACCOUNT_TYPES = ["Manager", "Admin", "Medical"];

function formatTime(date, fallbackString) {
  if (fallbackString) return fallbackString;
  const hours = String(date.getHours()).padStart(2, "0"); // Get hours and pad with leading zero if necessary
  const minutes = String(date.getMinutes()).padStart(2, "0"); // Get minutes and pad with leading zero if necessary
  return `${hours}:${minutes}`;
}

const getAppointmentRequestId = (data) =>
  data.appointment_request_id || data.parent_appointment_request_id || data.id;

const getStableAppointmentKey = (data) => data.occurrence_key || data.id;

const normalizedId = (value) => (value === null || value === undefined ? null : String(value));

const appointmentDisplayTime = (data) => {
  if (data.whole_day_reservation) return "Whole day";
  if (data.time_display || data.time_string) return data.time_display || data.time_string;
  if (!data.time) return "Time not provided";

  const parsedTime = new Date(data.time);
  return Number.isNaN(parsedTime.getTime()) ? "Time not provided" : formatTime(parsedTime);
};

export const appointmentUnavailableToPeer = (data, userId) => {
  const peerId = normalizedId(userId);
  if (!peerId || !Array.isArray(data.unavailable_peer_ids)) return false;

  return data.unavailable_peer_ids.some(
    (unavailablePeerId) => normalizedId(unavailablePeerId) === peerId
  );
};

export const AppointmentRequestActions = ({
  data,
  userId,
  isPeer,
  accountType,
  recurrenceScope,
  occurrenceStartAt,
  compact
}) => {
  const isMobile = useIsMobile();
  const [showManagerMatchSelector, setShowManagerMatchSelector] = useState(false);
  const [managerPeerOptions, setManagerPeerOptions] = useState([]);
  const [selectedManagerPeer, setSelectedManagerPeer] = useState(null);
  const [isLoadingPeers, setIsLoadingPeers] = useState(false);
  const [isManagerPeerDropdownOpen, setIsManagerPeerDropdownOpen] = useState(false);
  const [localRecurrenceScope, setLocalRecurrenceScope] = useState("occurrence");

  const resolvedAccountType = accountType || (isPeer ? "Peer" : "");
  const isPeerUser = resolvedAccountType === "Peer" || isPeer;
  const isManagerOrAdmin = MANAGER_OR_ADMIN_ACCOUNT_TYPES.includes(resolvedAccountType);
  const canMatchOrUnmatch = isPeerUser || isManagerOrAdmin;
  const appointmentRequestId = getAppointmentRequestId(data);
  const stableAppointmentKey = getStableAppointmentKey(data);
  const effectiveUserId =
    data.effective_user_id === undefined ? data.user_id : data.effective_user_id;
  const normalizedUserId = normalizedId(userId);
  const normalizedEffectiveUserId = normalizedId(effectiveUserId);
  const effectiveStatus = data.effective_status || data.status;
  const seriesStatus = data.expandable_series ? data.series_status : effectiveStatus;
  const seriesUserId = normalizedId(
    data.expandable_series ? data.series_user_id : normalizedEffectiveUserId
  );
  const unavailableToCurrentPeer =
    isPeerUser && appointmentUnavailableToPeer(data, normalizedUserId);
  const canActOnCurrentAssignment =
    isManagerOrAdmin ||
    !normalizedEffectiveUserId ||
    normalizedEffectiveUserId === normalizedUserId;
  const shouldScopeLifecycle = data.expandable_series && (canActOnCurrentAssignment || isPeerUser);
  const actionRecurrenceScope =
    recurrenceScope || (data.expandable_series ? localRecurrenceScope : null);
  const isSeriesAction = data.expandable_series && actionRecurrenceScope === "series";
  const actionOccurrenceStartAt = occurrenceStartAt || data.occurrence_start_at;
  const autoScopeId = `appointment-action-scope-${String(stableAppointmentKey).replace(
    /[^a-zA-Z0-9_-]/g,
    "-"
  )}`;
  const confirmationStatus = actionRecurrenceScope === "series" ? seriesStatus : effectiveStatus;
  const confirmationUserId =
    actionRecurrenceScope === "series" ? seriesUserId : normalizedEffectiveUserId;
  const canConfirmUpdate =
    confirmationStatus === "need_update_confirmation" &&
    (isManagerOrAdmin ||
      (isPeerUser && confirmationUserId === normalizedUserId && normalizedUserId)) &&
    !unavailableToCurrentPeer;
  const canEdit =
    isManagerOrAdmin ||
    (isPeerUser && normalizedEffectiveUserId === normalizedUserId && normalizedUserId);
  const {
    closeActionDialog,
    confirmPendingAction,
    goToEditAppointmentRequestPage,
    isSubmitting,
    openActionDialog,
    pendingAction,
    reason,
    reasonError,
    setReason,
    setReasonError
  } = useAppointmentRequestActions({
    data,
    appointmentRequestId,
    recurrenceScope: actionRecurrenceScope,
    occurrenceStartAt: actionOccurrenceStartAt,
    isManagerOrAdmin,
    selectedManagerPeer
  });

  const shouldUnmatchAppointment = useMemo(() => {
    if (isManagerOrAdmin) return !!normalizedEffectiveUserId;
    return !!normalizedUserId && normalizedEffectiveUserId === normalizedUserId;
  }, [isManagerOrAdmin, normalizedEffectiveUserId, normalizedUserId]);

  const actionButtonTextForUser = useMemo(() => {
    if (isManagerOrAdmin) return normalizedEffectiveUserId ? "Unmatch" : "Match";
    if (normalizedEffectiveUserId === normalizedUserId && normalizedUserId) return "Unmatch";
    if (unavailableToCurrentPeer) return "Unavailable to you";
    if (!normalizedEffectiveUserId) return "Match";
    return "Matched";
  }, [isManagerOrAdmin, normalizedEffectiveUserId, normalizedUserId, unavailableToCurrentPeer]);

  const shouldDisableMatchButton = useMemo(() => {
    if (!canMatchOrUnmatch) return true;
    if (isManagerOrAdmin) return showManagerMatchSelector && !normalizedEffectiveUserId;
    if (unavailableToCurrentPeer && !shouldUnmatchAppointment) return true;
    return !!normalizedEffectiveUserId && normalizedUserId !== normalizedEffectiveUserId;
  }, [
    canMatchOrUnmatch,
    isManagerOrAdmin,
    normalizedEffectiveUserId,
    normalizedUserId,
    shouldUnmatchAppointment,
    showManagerMatchSelector,
    unavailableToCurrentPeer
  ]);

  const shouldDisableDenyButton = useMemo(() => {
    if (!canMatchOrUnmatch) return true;
    if (isManagerOrAdmin) return false;
    return !!normalizedEffectiveUserId && normalizedUserId !== normalizedEffectiveUserId;
  }, [canMatchOrUnmatch, isManagerOrAdmin, normalizedEffectiveUserId, normalizedUserId]);

  const loadManagerPeerOptions = async () => {
    if (managerPeerOptions.length > 0) return managerPeerOptions;

    setIsLoadingPeers(true);
    try {
      const getPeersResponse = await axios.get("/peer_list.json");
      const mappedListOfPeers = (getPeersResponse.data || []).map((user) => ({
        label: user.full_name,
        value: user.id,
        id: user.id
      }));
      setManagerPeerOptions(mappedListOfPeers);
      return mappedListOfPeers;
    } catch (err) {
      toastr.error("Failed to load peers for matching. Please try again.");
      return [];
    } finally {
      setIsLoadingPeers(false);
    }
  };

  const openManagerMatchSelector = async () => {
    const options = await loadManagerPeerOptions();
    if (options.length === 0) return;
    setShowManagerMatchSelector(true);
    setIsManagerPeerDropdownOpen(true);
  };

  const closeManagerMatchSelector = () => {
    setShowManagerMatchSelector(false);
    setSelectedManagerPeer(null);
    setIsManagerPeerDropdownOpen(false);
  };

  const requestMatchOrUnmatchConfirmation = async () => {
    const isMatching = !shouldUnmatchAppointment;
    if (isManagerOrAdmin && isMatching) {
      if (!showManagerMatchSelector) {
        await openManagerMatchSelector();
        return;
      }
      if (!selectedManagerPeer) {
        toastr.warning("Please select a peer to match this appointment.");
        return;
      }
    }

    if (actionRecurrenceScope && !actionOccurrenceStartAt) {
      toastr.error("An occurrence start time is required for this recurring appointment action.");
      return;
    }

    setIsManagerPeerDropdownOpen(false);
    openActionDialog(isMatching ? "match" : "unmatch");
  };

  const selectedPeerName = selectedManagerPeer?.label;
  let scopeDescription = "";
  if (actionRecurrenceScope === "series") {
    scopeDescription =
      "This will update every occurrence in the recurring series and reset individual match overrides.";
  } else if (actionRecurrenceScope === "occurrence") {
    scopeDescription = "This will update only the selected occurrence.";
  }
  const actionDialogContent = {
    match: {
      title:
        actionRecurrenceScope === "series" ? "Confirm series match" : "Confirm appointment match",
      description: `${
        selectedPeerName
          ? `Match this appointment to ${selectedPeerName}?`
          : "Match this appointment to you?"
      }${scopeDescription ? ` ${scopeDescription}` : ""}`,
      confirmLabel: actionRecurrenceScope === "series" ? "Match entire series" : "Match appointment"
    },
    unmatch: {
      title:
        actionRecurrenceScope === "series"
          ? "Confirm series unmatch"
          : "Confirm appointment unmatch",
      description: `Remove the currently matched person from this appointment?${
        scopeDescription ? ` ${scopeDescription}` : ""
      }`,
      confirmLabel:
        actionRecurrenceScope === "series" ? "Unmatch entire series" : "Unmatch appointment"
    },
    deny: {
      title: isSeriesAction ? "Deny recurring series" : "Deny appointment",
      description: isSeriesAction
        ? "Give the appointment owner a brief reason. This denies the entire recurring series."
        : data.expandable_series
        ? "Give the appointment owner a brief reason. This denies only the selected occurrence."
        : "Give the appointment owner a brief reason for the denial.",
      confirmLabel: isSeriesAction
        ? "Deny entire series"
        : data.expandable_series
        ? "Deny occurrence"
        : "Deny appointment"
    },
    unavailable: {
      title: isSeriesAction
        ? "Mark yourself unavailable for this series"
        : "Mark yourself unavailable",
      description: `Give a brief reason you cannot take ${
        isSeriesAction
          ? "this recurring series"
          : data.expandable_series
          ? "this occurrence"
          : "this appointment"
      }.`,
      confirmLabel: isSeriesAction ? "Mark unavailable for series" : "Mark unavailable"
    },
    confirm_update: {
      title: "Confirm appointment update",
      description: "Confirm that you reviewed and accept the updated appointment details?",
      confirmLabel: "Confirm update"
    },
    delete: {
      title: isSeriesAction ? "Delete recurring series" : "Delete appointment",
      description: isSeriesAction
        ? "Delete every occurrence in this recurring series? This cannot be undone."
        : data.expandable_series
        ? "Delete only this appointment occurrence? This cannot be undone."
        : "Delete this appointment? This cannot be undone.",
      confirmLabel: isSeriesAction
        ? "Delete entire series"
        : data.expandable_series
        ? "Delete occurrence"
        : "Delete appointment"
    }
  }[pendingAction];

  const matchButtonVariantClass = (() => {
    if (shouldUnmatchAppointment) return "ccw-button--danger appointment-action--danger";
    if (unavailableToCurrentPeer) return "ccw-button--warning appointment-action--warning";
    return "ccw-button--primary appointment-action--primary";
  })();

  return (
    <AppointmentActionsContainer
      $isCompact={compact}
      data-appointment-key={stableAppointmentKey}
      data-appointment-request-id={appointmentRequestId}
    >
      {shouldScopeLifecycle && !recurrenceScope ? (
        <div className="peer_dashboard_recurrence_scope peer_dashboard_recurrence_scope--card">
          <label htmlFor={autoScopeId}>Apply action to</label>
          <select
            id={autoScopeId}
            explorator_test_id={`appointment-action-scope-${appointmentRequestId}`}
            className="ccw-select"
            value={localRecurrenceScope}
            onChange={(event) => setLocalRecurrenceScope(event.target.value)}
          >
            <option value="occurrence">This occurrence only</option>
            <option value="series">Entire recurring series</option>
          </select>
          <small>
            {localRecurrenceScope === "series"
              ? "Applies to every occurrence; individual occurrence overrides may be reset."
              : "Leaves the rest of the series unchanged."}
          </small>
        </div>
      ) : null}
      <StyledCardActions $isMobile={isMobile} $isCompact={compact}>
        <AppointmentPrimaryActions>
          {canMatchOrUnmatch && effectiveStatus !== "denied" ? (
            <Button
              explorator_test_id={`appointment-match-toggle-${appointmentRequestId}`}
              className={`ccw-button appointment-action ${matchButtonVariantClass}`}
              disabled={isSubmitting || shouldDisableMatchButton}
              onClick={requestMatchOrUnmatchConfirmation}
              size="small"
            >
              {actionButtonTextForUser}
            </Button>
          ) : null}
          {effectiveStatus !== "denied" && isPeerUser ? (
            <Button
              explorator_test_id={`appointment-mark-unavailable-${appointmentRequestId}`}
              className="ccw-button ccw-button--warning appointment-action appointment-action--warning"
              disabled={isSubmitting || unavailableToCurrentPeer}
              onClick={() => openActionDialog("unavailable")}
              size="small"
            >
              {unavailableToCurrentPeer ? "Unavailable recorded" : "Mark Unavailable"}
            </Button>
          ) : null}
          {canConfirmUpdate ? (
            <Button
              explorator_test_id={`appointment-confirm-update-${appointmentRequestId}`}
              className="ccw-button ccw-button--primary appointment-action appointment-action--primary"
              disabled={isSubmitting}
              onClick={() => openActionDialog("confirm_update")}
              size="small"
            >
              {isSeriesAction ? "Confirm Series Update" : "Confirm Update"}
            </Button>
          ) : null}
          {effectiveStatus !== "denied" && isManagerOrAdmin ? (
            <Button
              explorator_test_id={`appointment-deny-${appointmentRequestId}`}
              className="ccw-button ccw-button--danger appointment-action appointment-action--danger"
              disabled={isSubmitting || shouldDisableDenyButton}
              onClick={() => openActionDialog("deny")}
              size="small"
            >
              {isSeriesAction ? "Deny Series" : "Deny"}
            </Button>
          ) : null}
        </AppointmentPrimaryActions>
        {(canEdit || isManagerOrAdmin) && (
          <AppointmentUtilityActions $isMobile={isMobile}>
            {canEdit ? (
              <Button
                explorator_test_id={`appointment-edit-${appointmentRequestId}`}
                aria-label={
                  isMobile
                    ? isSeriesAction
                      ? "Edit recurring series"
                      : data.expandable_series
                      ? "Edit occurrence"
                      : "Edit"
                    : undefined
                }
                className={`ccw-button appointment-action appointment-action--neutral appointment-action--edit${
                  isMobile ? " appointment-action--icon" : ""
                }`}
                disabled={isSubmitting}
                onClick={goToEditAppointmentRequestPage}
                size="small"
                title={
                  isMobile
                    ? isSeriesAction
                      ? "Edit recurring series"
                      : data.expandable_series
                      ? "Edit occurrence"
                      : "Edit"
                    : undefined
                }
              >
                {isMobile ? (
                  <>
                    <EditOutlinedIcon aria-hidden="true" fontSize="small" />
                    <span className="peer_dashboard_visually_hidden">
                      {isSeriesAction
                        ? "Edit recurring series"
                        : data.expandable_series
                        ? "Edit occurrence"
                        : "Edit"}
                    </span>
                  </>
                ) : isSeriesAction ? (
                  "Edit Series"
                ) : data.expandable_series ? (
                  "Edit Occurrence"
                ) : (
                  "Edit"
                )}
              </Button>
            ) : null}
            {isManagerOrAdmin ? (
              <Button
                explorator_test_id={`appointment-delete-${appointmentRequestId}`}
                aria-label={
                  isMobile
                    ? isSeriesAction
                      ? "Delete recurring series"
                      : data.expandable_series
                      ? "Delete occurrence"
                      : "Delete"
                    : undefined
                }
                className={`ccw-button ccw-button--danger appointment-action appointment-action--danger appointment-action--delete${
                  isMobile ? " appointment-action--icon" : ""
                }`}
                color="error"
                disabled={isSubmitting}
                onClick={() => openActionDialog("delete")}
                size="small"
                title={
                  isMobile
                    ? isSeriesAction
                      ? "Delete recurring series"
                      : data.expandable_series
                      ? "Delete occurrence"
                      : "Delete"
                    : undefined
                }
              >
                {isMobile ? (
                  <>
                    <DeleteOutlineIcon aria-hidden="true" fontSize="small" />
                    <span className="peer_dashboard_visually_hidden">
                      {isSeriesAction
                        ? "Delete recurring series"
                        : data.expandable_series
                        ? "Delete occurrence"
                        : "Delete"}
                    </span>
                  </>
                ) : isSeriesAction ? (
                  "Delete Series"
                ) : data.expandable_series ? (
                  "Delete Occurrence"
                ) : (
                  "Delete"
                )}
              </Button>
            ) : null}
          </AppointmentUtilityActions>
        )}
      </StyledCardActions>
      {isManagerOrAdmin && !effectiveUserId && showManagerMatchSelector ? (
        <ManagerMatchContainer $isCompact={compact}>
          <StyledManagerAutocomplete
            explorator_test_id={`appointment-match-select-${appointmentRequestId}`}
            options={managerPeerOptions}
            size="small"
            value={selectedManagerPeer}
            loading={isLoadingPeers}
            open={isManagerPeerDropdownOpen}
            openOnFocus
            autoHighlight
            onOpen={() => setIsManagerPeerDropdownOpen(true)}
            onClose={() => setIsManagerPeerDropdownOpen(false)}
            onChange={(_, value) => {
              setSelectedManagerPeer(value);
              setIsManagerPeerDropdownOpen(false);
            }}
            isOptionEqualToValue={(option, value) => !!value && option.id === value.id}
            getOptionLabel={(option) => option?.label || ""}
            PopperComponent={ManagerAutocompletePopper}
            renderInput={(params) => <StyledManagerTextField {...params} label="Peer to match" />}
          />
          <ManagerMatchActions>
            <Button
              explorator_test_id={`appointment-confirm-match-${appointmentRequestId}`}
              className="ccw-button ccw-button--primary"
              size="small"
              disabled={isSubmitting || !selectedManagerPeer}
              onClick={requestMatchOrUnmatchConfirmation}
            >
              Confirm
            </Button>
            <Button
              className="ccw-button"
              explorator_test_id={`appointment-cancel-match-${appointmentRequestId}`}
              disabled={isSubmitting}
              size="small"
              onClick={closeManagerMatchSelector}
            >
              Cancel
            </Button>
          </ManagerMatchActions>
        </ManagerMatchContainer>
      ) : null}
      <Dialog
        className="peer_dashboard_action_dialog"
        open={Boolean(pendingAction)}
        onClose={closeActionDialog}
        aria-label={actionDialogContent?.title || "Confirm appointment action"}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: "ccw-dialog" }}
      >
        <DialogTitle>{actionDialogContent?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{actionDialogContent?.description}</DialogContentText>
          {["deny", "unavailable"].includes(pendingAction) ? (
            <TextField
              explorator_test_id={`appointment-action-reason-${appointmentRequestId}`}
              autoFocus
              disabled={isSubmitting}
              error={Boolean(reasonError)}
              fullWidth
              helperText={reasonError || "Required"}
              label="Reason"
              margin="normal"
              minRows={3}
              multiline
              onChange={(event) => {
                setReason(event.target.value);
                if (reasonError) setReasonError("");
              }}
              required
              value={reason}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            className="ccw-button"
            explorator_test_id={`appointment-cancel-action-${appointmentRequestId}`}
            disabled={isSubmitting}
            onClick={closeActionDialog}
          >
            Cancel
          </Button>
          <Button
            explorator_test_id={`appointment-confirm-action-${appointmentRequestId}`}
            className="ccw-button ccw-button--primary"
            disabled={isSubmitting}
            onClick={confirmPendingAction}
            variant="contained"
          >
            {isSubmitting ? "Working…" : actionDialogContent?.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </AppointmentActionsContainer>
  );
};

AppointmentRequestActions.propTypes = {
  data: PropTypes.object.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isPeer: PropTypes.bool,
  accountType: PropTypes.string,
  recurrenceScope: PropTypes.oneOf(["occurrence", "series"]),
  occurrenceStartAt: PropTypes.string,
  compact: PropTypes.bool
};

AppointmentRequestActions.defaultProps = {
  userId: null,
  isPeer: false,
  accountType: null,
  recurrenceScope: null,
  occurrenceStartAt: null,
  compact: true
};

const AppointmentRequestSimpleDisplay = ({
  data,
  userId,
  isPeer,
  accountType,
  isHighlighted,
  actionsOnly,
  recurrenceScope,
  occurrenceStartAt
}) => {
  const isMobile = useIsMobile();

  const formatPreview = (text, limit = 120) => {
    if (!text) return "";
    if (text.length <= limit) return text;
    return `${text.substring(0, limit)}...`;
  };

  const clientName = useMemo(() => {
    if (data.client_name) return data.client_name;
    if (data.todo_client_name) return data.todo_client_name;
    return "Client not provided";
  }, [data.client_name, data.todo_client_name]);

  const appointmentDescription = useMemo(() => {
    if (data.description) return data.description;
    if (data.task_description) return data.task_description;
    return "Description not provided";
  }, [data.description, data.task_description]);

  const cardHighlightStyles = {};
  if (isHighlighted) {
    cardHighlightStyles.border = "2px solid #f59e0b";
    cardHighlightStyles.backgroundColor = "#fff9e8";
  }

  const actionProps = {
    data,
    userId,
    isPeer,
    accountType,
    recurrenceScope,
    occurrenceStartAt
  };

  if (actionsOnly) return <AppointmentRequestActions {...actionProps} />;

  const status = statusMetadata(data.effective_status || data.status);
  const recurring =
    data.expandable_series || data.is_recurring || data.recurring || data.recurrence_label;

  return (
    <Card
      explorator_test_id={`appointment-card-${getAppointmentRequestId(data)}`}
      className="ccw-surface peer_dashboard_request_card"
      data-appointment-key={getStableAppointmentKey(data)}
      data-appointment-request-id={getAppointmentRequestId(data)}
      sx={{
        border: "1px solid var(--ccw-color-border-strong)",
        borderRadius: "var(--ccw-surface-radius)",
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        minWidth: isMobile ? "auto" : 250,
        maxWidth: isMobile ? "100%" : 300,
        width: isMobile ? "100%" : "auto",
        ...cardHighlightStyles
      }}
    >
      <CardContent className="peer_dashboard_request_card_content">
        <div className="peer_dashboard_mobile_agenda_heading peer_dashboard_request_card_heading">
          <div>
            <p>{data.date || "Date not provided"}</p>
            <h3>{clientName}</h3>
            <span>
              {appointmentDisplayTime(data)} · Client #{appointmentClientNumber(data)}
            </span>
          </div>
          <StatusBadge
            className={`peer_dashboard_status_pill ${status.className}`}
            label={status.label}
            status={data.effective_status || data.status}
          />
        </div>
        <dl className="peer_dashboard_mobile_agenda_details peer_dashboard_request_card_details">
          <div>
            <dt>Unit</dt>
            <dd>{appointmentUnit(data)}</dd>
          </div>
          <div>
            <dt>Pickup</dt>
            <dd>{appointmentPickup(data)}</dd>
          </div>
          <div>
            <dt>Destination</dt>
            <dd>{appointmentDestination(data)}</dd>
          </div>
          <div>
            <dt>Matched Peer</dt>
            <dd>{appointmentMatchedPeer(data)}</dd>
          </div>
          <div>
            <dt>Recurrence</dt>
            <dd>
              {recurring && <span aria-hidden="true">↻ </span>}
              {appointmentRecurrence(data)}
            </dd>
          </div>
          <div className="peer_dashboard_request_card_detail--wide">
            <dt>Description</dt>
            <dd>{formatPreview(appointmentDescription)}</dd>
          </div>
          {data.suggested_transportation && (
            <div className="peer_dashboard_request_card_detail--wide">
              <dt>Transport</dt>
              <dd>
                {data.suggested_transportation}
                {data.suggested_transportation === "Uber" && data.uber_approver
                  ? ` (approved by ${data.uber_approver})`
                  : ""}
              </dd>
            </div>
          )}
          {data.safety_considerations && (
            <div className="peer_dashboard_request_card_detail--wide">
              <dt>Safety</dt>
              <dd>{formatPreview(data.safety_considerations)}</dd>
            </div>
          )}
        </dl>
        {data.peer_unavailabilities_count > 0 && (
          <Typography variant="body2" sx={{ mt: 0.5, color: "#b45309", fontWeight: 500 }}>
            {data.peer_unavailabilities_count} peer(s) unavailable
          </Typography>
        )}
        {data.peer_unavailabilities_details && data.peer_unavailabilities_details.length > 0 && (
          <Typography variant="body2" sx={{ mt: 0.25, color: "#92400e", fontSize: "1rem" }}>
            {data.peer_unavailabilities_details
              .map((u) => `${u.first_name} ${u.last_name}: ${u.reason}`)
              .join("; ")}
          </Typography>
        )}
      </CardContent>
      <AppointmentRequestActions {...actionProps} compact={false} />
    </Card>
  );
};

const StyledCardActions = styled(CardActions)`
  && {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    gap: var(--ccw-space-2);
    margin-top: auto;
    padding: ${({ $isCompact }) => ($isCompact ? "0" : "8px")};
  }

  && .appointment-action {
    font-size: 1rem;
    height: ${({ $isMobile }) => ($isMobile ? "var(--ccw-control-height)" : "auto")};
    line-height: 1.1;
    min-height: var(--ccw-control-height);
    min-width: 0;
    padding-left: var(--ccw-space-2);
    padding-right: var(--ccw-space-2);
    white-space: ${({ $isMobile }) => ($isMobile ? "nowrap" : "normal")};
    width: 100%;
  }

  && .appointment-action--icon {
    padding-left: var(--ccw-space-2);
    padding-right: var(--ccw-space-2);
  }

  && > :not(:first-child) {
    margin-left: 0;
  }
`;

const AppointmentPrimaryActions = styled.div`
  display: grid;
  gap: var(--ccw-space-2);
  grid-template-columns: repeat(2, minmax(0, 1fr));

  && > .appointment-action:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }
`;

const AppointmentUtilityActions = styled.div`
  display: ${({ $isMobile }) => ($isMobile ? "flex" : "grid")};
  gap: var(--ccw-space-2);
  grid-template-columns: repeat(2, minmax(0, 1fr));
  justify-content: center;

  && > .appointment-action--icon {
    flex: 0 0 var(--ccw-control-height);
    width: var(--ccw-control-height);
  }
`;

AppointmentRequestSimpleDisplay.propTypes = {
  data: PropTypes.object.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isPeer: PropTypes.bool,
  accountType: PropTypes.string,
  isHighlighted: PropTypes.bool,
  actionsOnly: PropTypes.bool,
  recurrenceScope: PropTypes.oneOf(["occurrence", "series"]),
  occurrenceStartAt: PropTypes.string
};

AppointmentRequestSimpleDisplay.defaultProps = {
  userId: null,
  isPeer: false,
  accountType: null,
  isHighlighted: false,
  actionsOnly: false,
  recurrenceScope: null,
  occurrenceStartAt: null
};

export default AppointmentRequestSimpleDisplay;

const AppointmentActionsContainer = styled.div`
  min-width: ${({ $isCompact }) => ($isCompact ? "12rem" : "0")};
  width: ${({ $isCompact }) => ($isCompact ? "100%" : "auto")};
`;

const ManagerMatchContainer = styled.div`
  padding: ${({ $isCompact }) => ($isCompact ? "0.5rem 0 0" : "0 0.75rem 0.75rem")};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StyledManagerAutocomplete = styled(Autocomplete)`
  && {
    width: 100%;
  }
`;

const ManagerAutocompletePopper = styled(Popper)`
  && {
    z-index: 1600;
  }
`;

const StyledManagerTextField = styled(TextField)`
  && {
    background-color: white;
  }
`;

const ManagerMatchActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;
